import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { useUserData } from "features/userData/context";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { hasProviderOperationsAccess } from "features/providerOperations/hasProviderOperationsAccess";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { MenuLink } from "types";
import { ProviderOperationsPage } from "../ProviderOperationsPage";
import Section from "./Section";
import { Sidebar } from "./Sidebar";
import { getMenuItems } from "./functions";
import { isProductManagementLeaguesPath } from "./isProductManagementLeaguesPath";
import { resolveProductManagementActiveMenu } from "./resolveProductManagementActiveMenu";

function getProductManagementMenuUrl(menuId: string): string {
	return `/${MenuLink.PRODUCT_MANAGEMENT}?menu=${encodeURIComponent(menuId)}`;
}

export function ProductManagement() {
	const userData = useUserData();
	const { productManagementOptions, isSuperAdmin } = userData;
	const navigate = useNavigate();
	const location = useLocation();
	const isLeaguesPage = isProductManagementLeaguesPath(location.pathname);
	const [searchParams] = useSearchParams();
	const requestedMenu = searchParams.get("menu");
	const canShowProviderOperations = hasProviderOperationsAccess(userData);
	const isProviderOperationsRequestedWithoutAccess =
		requestedMenu === MenuLink.PROVIDER_OPERATIONS && canShowProviderOperations === false;
	const activeMenu = resolveProductManagementActiveMenu({
		requestedMenu,
		canShowProviderOperations,
		productManagementOptions,
		isSuperAdmin,
	});

	useEffect(() => {
		setScrollBehavior();
	}, []);

	const onMenuClick = (id: string) => {
		navigate(getProductManagementMenuUrl(id));
	};

	const menuItems = getMenuItems(productManagementOptions);
	if (canShowProviderOperations) {
		menuItems.push({
			id: MenuLink.PROVIDER_OPERATIONS,
			name: _("header.admin.providerOperations" as keyof Keys),
			icon: "admin-syc-new",
			hasSubMenu: false,
			subMenuItems: [],
		});
	}

	if (isProviderOperationsRequestedWithoutAccess && menuItems.length === 0) {
		return <ProviderOperationsPage />;
	}

	return (
		<div className="product-management-container">
			<Sidebar onMenuClick={onMenuClick} activeMenu={activeMenu} menuItems={menuItems} />
			{isLeaguesPage ? (
				<section className="home-section">
					<div className="home-content product-management-right">
						<Outlet />
					</div>
				</section>
			) : (
				<Section activeMenu={activeMenu} onMenuClick={onMenuClick} />
			)}
		</div>
	);
}
